import { FC, useContext, useEffect, useState } from 'react';
import { Box, Spinner } from '@chakra-ui/react';

import { 
  sortEntityByName} from '@app/utils';
import { useEntityState } from '@app/hooks/use-entity-state';
import AutocompleteField from '@app/components/common/autocomplete-field';
import SelectField from '@app/components/common/select-field';
import TextField from '@app/components/common/text-field';
import { useGuide } from '@app/hooks/use-guide';
import { useAutocomplete, usePaginatedAutocomplete } from '@app/hooks/use-autocomplete';
import { useAlert } from '@app/hooks/use-alert';
import { Form } from '@app/components/common/form/form';
import { FieldComponentProps } from '@app/components/common/types';
import { VisitFrequencySelectField } from '@app/components/common/visit-frequency';
import { OutletField } from '@app/pages/equipments/dialogs/components/OutletField';
import { MasterOutlet } from '@app/api/models';
import { useApi } from '@app/hooks';
import { FieldView } from '@app/components/common';
import { ProfileContext } from '@app/contexts/profile';

import { validateForm } from './utils/validation';
import { Channel, UpdateOutletFormInstance } from './types';
import { RouteNumberField } from '../outlets/components/route-number-field';
import { ContactFields } from '../outlets/components/contact-fields';

interface OutletFormProps {
  instance: Partial<UpdateOutletFormInstance>;
  submitText?: string;
  onSubmit: (instance: Partial<UpdateOutletFormInstance>) => void;
  onCancel: () => void;
}

const UpdateOutletForm: FC<OutletFormProps> = (props) => {
  const api = useApi();
  const { warning } = useAlert();
  const { profile } = useContext(ProfileContext);
  const isRouteNumberLocked = Number(profile?.position_id) === 3;
  const {
    instance,
    submitText,
    onSubmit,
    onCancel,
  } = props;

  const [outlet, onChange, setInstance] =
    useEntityState<UpdateOutletFormInstance>(instance);
  const [loading, setLoading] = useState(false);

  const [
    {
      statuses,
      channels,
      subChannels,
    },
    setGuides,
  ] = useState<Record<string, EntityBase[]>>({
    statuses: [],
    channels: [],
    subChannels: [],
    rawChannels: [],
  });

  const getChannels = useGuide('outlet_sub_channel', undefined, setLoading);
  const getStatuses = useGuide('outlet_status', undefined, setLoading);
  const getAutocomplete = useAutocomplete();
  const getPaginatedAutocomplete = usePaginatedAutocomplete();

  useEffect(() => {
    (async () => {
      const channels = (await getChannels()) as Channel[];
      const statuses = (await getStatuses()) as EntityBase[];
      const guides = {
        statuses,
        rawChannels: channels,
        channels: sortEntityByName(
          channels.reduce<EntityBase[]>((acc, item, index) => {
            if (acc.find(({ name }) => name === item.channel)) {
              return acc;
            }
            return [...acc, { id: index, name: item.channel }];
          }, [])
        ),
        subChannels: [],
      };

      setGuides(guides);

      setLoading(false);
    })();
  }, []);

  const onSubmitForm = async () => {
    const { isValid, errors } = await validateForm(outlet);
    if (!isValid) {
      warning(Object.values(errors).join('. '));
      return;
    }

    const result = {
      ...outlet,
    };

    onSubmit(result);
  };

  const onOutletChange = async (outlet: MasterOutlet) => {

    if (!outlet) {
      setInstance({});
      return;
    }
    const { data } = await api.acb.getMasterOutlet(outlet.id);
    outlet = data;
    setInstance({
      ...instance,
      outlet,
      outlet_id: outlet.id,
      status: outlet.status,
      name: outlet.name,
      title: outlet.title,
      subnet: outlet.subnet,
      distributor: outlet.distributor,
      channel: outlet.channel, 
      sub_channel: outlet.sub_channel,
      address: {
        full_address: outlet.address?.full_address,
        region: { name: outlet.address?.region },
        city: { name: outlet.address?.city },
        address: outlet.address?.address,
        latlng: outlet.address?.latlng,
      },
      director: outlet.director,
      visits_day: outlet.visits_day,
      phone: outlet.phone,
      email: outlet.email,
      contact_person_1: outlet.contact_person_1,
      contact_person_2: outlet.contact_person_2,

    });
  };


  if (loading) {
    return <Box
      position="absolute"
      style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
      top={0}
      left={0}
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Spinner />
    </Box>;
  }
  
  return (<>
    <Form 
      instance={outlet}   
      onSubmit={onSubmitForm}
      submitText={submitText} 
      onCancel={onCancel}    
      onChange={onChange}
    >
      <Form.Item
        label="Изменяемый ТТ"
        field='outlet'
        component={OutletField}
        onChange={onOutletChange}
        componentProps={{ status: 'all'}}
        span={{ md: 12 }}
      />      
      <Form.Divider />
      <Form.Item
        required
        label="Наименование"
        field='name'
        component={TextField}
        componentProps={{ disabled: !outlet.outlet }} 
        span={{ md: 12 }}
      />
      <Form.Item
        label="Подсеть"
        field='subnet'
        component={TextField}
        componentProps={{ disabled: !outlet.outlet }} 
        span={{ md: 6 }}
      />
      <Form.Item
        required
        label="Вывеска"
        field='title'
        component={TextField}
        componentProps={{ disabled: !outlet.outlet }} 
        span={{ md: 6 }}
      />
      <Form.Divider />
      <Form.Item
        required
        label="Статус"
        field='status'
        component={SelectField}
        componentProps={{
          options: statuses,
          disabled: !outlet.outlet,
        }}
        span={{ md: 6 }}
      />
      <Form.Item
        required
        label="Дистрибьютор"
        field='distributor'
        component={AutocompleteField}
        componentProps={{
          name: 'distributor',
          valueProperty: 'name',
          onFetchOptions: getAutocomplete,
          disabled: !outlet.outlet
        }}
        span={{ md: 6 }}
      />
      <Form.Item
        required
        label="Канал"
        field='channel'
        component={SelectField}
        componentProps={{
          options: channels,
          disabled: !outlet.outlet
        }}
        onChange={(value) => {
          onChange('sub_channel')(null);
          setGuides((guides) => ({
            ...guides,
            subChannels: sortEntityByName(
              guides.rawChannels.filter((item) => {
                return (
                  item.channel?.toUpperCase() ===
                  (value?.name || '').toUpperCase()
                );
              })
            ),
          }));
        }}
        span={{ md: 6 }}
      />
      <Form.Item
        required
        label="Подканал"
        field='sub_channel'
        component={SelectField}
        componentProps={{
          options: subChannels,
          disabled: !outlet.outlet
        }}
        span={{ md: 6 }}
      />
      <Form.Item
        label="День визита в ТТ"
        field='visits_day'
        required
        component={VisitFrequencySelectField}
        componentProps={{ 
          disabled: !outlet.outlet,
          disabledZeroOption: true,
        }} 
        span={{ md: 6 }}
      />
      <Form.Item
        label="Номер маршрута"
        field='route_number'
        component={RouteNumberField}
        componentProps={{
          name: 'route_number',
          valueProperty: 'name',
          onFetchOptions: getPaginatedAutocomplete,
          disabled: !outlet.outlet || isRouteNumberLocked,
        }}
        span={{ md: 6 }}
      />
      <Form.Divider />
      <Form.Item
        required
        label="Фактический адрес"
        field='address'
        component={({ value, ...props }: FieldComponentProps) => <AutocompleteField
          id="address"
          value={value?.full_address}
          {...props}        
        />}
        componentProps={{
          name: 'dd/address',
          valueProperty: 'address',
          onFetchOptions: getAutocomplete,
          disabled: !outlet.outlet,
        }}
        span={{ md: 12 }}
      />
      <Form.Divider />
      <Form.Item
        label="Директор - ФИО"
        field='director'
        component={TextField}
        componentProps={{ disabled: !outlet.outlet }} 
        span={{ md: 6 }} 
      />
      <Form.Item
        label="Tелефон"
        field='phone'
        component={TextField}
        componentProps={{ disabled: !outlet.outlet }} 
        span={{ md: 3 }} 
      />
      <Form.Item
        label="Email"
        field='email'
        component={TextField}
        componentProps={{ disabled: !outlet.outlet }} 
        span={{ md: 3 }} 
      />
      <Form.OutletItem
        label="Контактное лицо (1)"
        field='contact_person_1'
        component={ContactFields}
        componentProps={{ instance, field: 'contact_person_1', disabled: !outlet.outlet }}
      />
      <Form.OutletItem
        label="Контактное лицо (2)"
        field='contact_person_2'
        component={ContactFields}
        componentProps={{ origin, instance, field: 'contact_person_2', disabled: !outlet.outlet }}
      />
      <Form.Divider />
      <Form.Spacer span={{ md: 12 }} >
        <Box style={{ gap: 8 }} display='flex' flexDirection='column' alignItems='flex-end'> 
          <FieldView title="Количество холодильников:" value={outlet?.outlet?.count_equipment || '0'} direction='row' />
          <FieldView title="Количество стоек:" value={outlet?.outlet?.count_stand || '0'} direction='row' />
        </Box>
      </Form.Spacer>
    </Form>
  </>);
};

export default UpdateOutletForm;
